import { supabase } from '../config/supabase';
import { Product } from '../types';
import { deleteImageFromSupabase } from './supabaseStorage';
import { STORAGE_BUCKETS } from '../config/supabase';

/**
 * Map Supabase product row to Product type
 */
const mapSupabaseProduct = (row: any): Product => {
  // Parse stockBySize from JSONB or JSON string
  let stockBySize: Record<string, number> | undefined = undefined;
  if (row.stock_by_size) {
    if (typeof row.stock_by_size === 'string') {
      try {
        stockBySize = JSON.parse(row.stock_by_size);
      } catch (e) {
        console.error('Error parsing stock_by_size:', e);
      }
    } else if (typeof row.stock_by_size === 'object') {
      stockBySize = row.stock_by_size;
    }
  }
  
  return {
    id: row.id,
    name: row.name,
    description: row.description || undefined,
    price: parseFloat(row.price),
    originalPrice: row.original_price ? parseFloat(row.original_price) : undefined,
    image: row.image || undefined,
    images: row.images || [],
    category: row.category,
    subcategory: row.subcategory || undefined,
    available: row.available ?? true,
    preOrder: row.pre_order ?? false,
    featured: row.featured ?? false,
    mostSold: row.most_sold ?? false,
    newArrival: row.new_arrival ?? false,
    features: row.features || [],
    sizes: row.sizes || [],
    stockQuantity: row.stock_quantity || undefined,
    stockBySize: stockBySize,
    lowStockThreshold: row.low_stock_threshold || undefined
  };
};

/**
 * Map Product type to Supabase row format
 */
const mapToSupabaseProduct = (product: Partial<Product>): any => {
  return {
    name: product.name,
    description: product.description || null,
    price: product.price,
    original_price: product.originalPrice || null,
    image: product.image || null,
    images: product.images || [],
    category: product.category,
    subcategory: product.subcategory || null,
    available: product.available ?? true,
    pre_order: product.preOrder ?? false,
    featured: product.featured ?? false,
    most_sold: product.mostSold ?? false,
    new_arrival: product.newArrival ?? false,
    features: product.features || [],
    sizes: product.sizes || [],
    stock_quantity: product.stockQuantity || null,
    stock_by_size: product.stockBySize && Object.keys(product.stockBySize).length > 0 
      ? product.stockBySize 
      : null,
    low_stock_threshold: product.lowStockThreshold || null
  };
};

/**
 * Fetch all products from Supabase
 */
export const fetchProductsFromSupabase = async (): Promise<Product[]> => {
  try {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching products:', error);
      throw error;
    }

    return (data || []).map(mapSupabaseProduct);
  } catch (error) {
    console.error('Error in fetchProductsFromSupabase:', error);
    return [];
  }
};

/**
 * Add product to Supabase
 */
export const addProductToSupabase = async (productData: Partial<Product>): Promise<Product> => {
  try {
    const supabaseProduct = mapToSupabaseProduct(productData);
    
    const { data, error } = await supabase
      .from('products')
      .insert([supabaseProduct])
      .select()
      .single();

    if (error) {
      console.error('Error adding product:', error);
      throw error;
    }

    return mapSupabaseProduct(data);
  } catch (error) {
    console.error('Error in addProductToSupabase:', error);
    throw error;
  }
};

/**
 * Update product in Supabase
 */
export const updateProductInSupabase = async (
  id: string | number,
  productData: Partial<Product>
): Promise<Product> => {
  try {
    const supabaseProduct = mapToSupabaseProduct(productData);
    
    const { data, error } = await supabase
      .from('products')
      .update(supabaseProduct)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating product:', error);
      throw error;
    }

    return mapSupabaseProduct(data);
  } catch (error) {
    console.error('Error in updateProductInSupabase:', error);
    throw error;
  }
};

/**
 * Delete product from Supabase (and its images from storage)
 */
export const deleteProductFromSupabase = async (id: string | number): Promise<void> => {
  try {
    // First, get the product to delete its images
    const { data: product, error: fetchError } = await supabase
      .from('products')
      .select('image, images')
      .eq('id', id)
      .single();

    if (fetchError) {
      console.error('Error fetching product for deletion:', fetchError);
    } else if (product) {
      // Delete images from storage
      if (product.image && product.image.includes('supabase.co')) {
        await deleteImageFromSupabase(product.image, STORAGE_BUCKETS.PRODUCT_IMAGES);
      }
      if (product.images && Array.isArray(product.images)) {
        for (const imageUrl of product.images) {
          if (imageUrl && imageUrl.includes('supabase.co')) {
            await deleteImageFromSupabase(imageUrl, STORAGE_BUCKETS.PRODUCT_IMAGES);
          }
        }
      }
    }

    // Delete product from database
    const { error } = await supabase
      .from('products')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error in deleteProductFromSupabase:', error);
    throw error;
  }
};

/**
 * Get product by ID from Supabase
 */
export const getProductByIdFromSupabase = async (id: string | number): Promise<Product | null> => {
  try {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows returned
        return null;
      }
      console.error('Error fetching product:', error);
      throw error;
    }

    return mapSupabaseProduct(data);
  } catch (error) {
    console.error('Error in getProductByIdFromSupabase:', error);
    return null;
  }
};
